/** The agency's portal controls: sharing a document, branding, and each client's logins. */

import { apiCall, orgPath } from '../api.ts';
import type { Branding } from '../portal/portal-api.ts';
import type { ReportDocument } from './reports-api.ts';

export interface ClientPerson {
  user_id: string;
  email: string;
  name: string | null;
  since: string;
}

export interface ClientInvitation {
  id: string;
  email: string;
  expires_at: string;
  created_at: string;
}

const reports = (org: string, path: string) => orgPath(org, `/reports${path}`);

export const agencyApi = {
  setShared: (org: string, documentId: string, shared: boolean) =>
    apiCall<{ document: ReportDocument }>(reports(org, `/documents/${documentId}/sharing`), {
      method: 'PUT',
      body: JSON.stringify({ shared }),
    }).then((data) => data.document),
  /** Shares or unshares, reporting the new state or a readable problem. */
  share: async (
    org: string,
    documentId: string,
    shared: boolean,
    onShared: (sharedAt: string | null) => void,
    onProblem: (message: string) => void,
  ) => {
    try {
      const document = await agencyApi.setShared(org, documentId, shared);
      onShared(document.shared_at ?? null);
    } catch (error) {
      onProblem(error instanceof Error ? error.message : 'Sharing could not be changed.');
    }
  },
  branding: (org: string) =>
    apiCall<{ branding: Branding }>(reports(org, '/branding')).then((data) => data.branding),
  saveBranding: (org: string, branding: Branding) =>
    apiCall<{ branding: Branding }>(reports(org, '/branding'), {
      method: 'PUT',
      body: JSON.stringify(branding),
    }).then((data) => data.branding),
  people: (org: string, clientId: string) =>
    apiCall<{ people: ClientPerson[]; invitations: ClientInvitation[] }>(
      reports(org, `/clients/${clientId}/people`),
    ),
  invite: (org: string, clientId: string, email: string) =>
    apiCall<unknown>(reports(org, `/clients/${clientId}/invitations`), {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  removeLogin: (org: string, clientId: string, userId: string) =>
    apiCall<unknown>(reports(org, `/clients/${clientId}/people/${userId}`), { method: 'DELETE' }),
  cancelInvitation: (org: string, invitationId: string) =>
    apiCall<unknown>(orgPath(org, `/invitations/${invitationId}`), { method: 'DELETE' }),
};
